import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Container,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  Stack,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  Scatter,
  ScatterChart,
  ZAxis
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InsightsIcon from '@mui/icons-material/Insights';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AnimatedNumber from '../../components/common/AnimatedNumber';

// Renk paleti
const COLORS = ['#4caf50', '#ff9800', '#f44336', '#3f51b5', '#9c27b0', '#e91e63', '#2196f3', '#00bcd4'];

// Demo veri
const generateInventoryData = () => {
  // Kategoriler
  const categories = ['Elektronik', 'Giyim', 'Ev Aletleri', 'Mobilya', 'Kozmetik', 'Gıda'];
  
  // Depolar
  const warehouses = ['İstanbul Merkez', 'Ankara Depo', 'İzmir Depo', 'Antalya Depo'];

  // Örnek ürünler için isimler
  const productNames = {
    'Elektronik': ['Akıllı Telefon', 'Laptop', 'Tablet', 'Kulaklık', 'Televizyon', 'Kamera'],
    'Giyim': ['T-shirt', 'Pantolon', 'Elbise', 'Ceket', 'Ayakkabı', 'Kazak'],
    'Ev Aletleri': ['Buzdolabı', 'Çamaşır Makinesi', 'Fırın', 'Mikser', 'Tost Makinesi'],
    'Mobilya': ['Koltuk', 'Masa', 'Sandalye', 'Dolap', 'Yatak'],
    'Kozmetik': ['Parfüm', 'Ruj', 'Krem', 'Şampuan', 'Maskara'],
    'Gıda': ['Kahve', 'Çikolata', 'Makarna', 'Pirinç', 'Konserve']
  };

  // Stok durumu risk seviyeleri
  const stockStatus = ['Kritik', 'Düşük', 'Normal', 'Yüksek'];

  // Ürün listesi
  const products = [];
  let id = 1;

  categories.forEach(category => {
    const categoryProducts = productNames[category];
    
    if (categoryProducts) { // Null check ekleyelim
      categoryProducts.forEach(name => {
        const warehouse = warehouses[Math.floor(Math.random() * warehouses.length)];
        const currentStock = Math.floor(Math.random() * 1000);
        const minStock = Math.floor(currentStock * (0.2 + Math.random() * 0.3));
        const maxStock = currentStock * (1.5 + Math.random());
        const reorderPoint = minStock + Math.floor((maxStock - minStock) * 0.3);
        
        // Stok durumu hesaplama
        let status;
        if (currentStock < minStock) {
          status = 'Kritik';
        } else if (currentStock < reorderPoint) {
          status = 'Düşük';
        } else if (currentStock > maxStock * 0.8) {
          status = 'Yüksek';
        } else {
          status = 'Normal';
        }

        const turnoverRate = (1 + Math.random() * 2).toFixed(2); // Stok devir hızı
        const profitMargin = (5 + Math.random() * 25).toFixed(1); // Kar marjı
        const leadTime = Math.floor(2 + Math.random() * 10); // Tedarik süresi
        const salesVelocity = Math.floor(currentStock / (Math.random() * 20 + 5)); // Satış hızı
        const costPerUnit = Math.floor(20 + Math.random() * 980); // Birim maliyet
        const stockValue = currentStock * costPerUnit; // Stok değeri

        products.push({
          id: id++,
          name,
          category,
          warehouse,
          currentStock,
          minStock,
          maxStock,
          reorderPoint,
          status,
          turnoverRate,
          profitMargin,
          leadTime,
          salesVelocity,
          costPerUnit,
          stockValue,
          lastUpdated: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        });
      });
    }
  });

  return {
    products,
    summary: {
      totalProducts: products.length,
      totalInventoryValue: products.reduce((sum, p) => sum + p.stockValue, 0),
      avgTurnoverRate: (products.reduce((sum, p) => sum + parseFloat(p.turnoverRate), 0) / products.length).toFixed(2),
      criticalStockCount: products.filter(p => p.status === 'Kritik').length,
      lowStockCount: products.filter(p => p.status === 'Düşük').length,
      optimizedStockCount: products.filter(p => p.status === 'Normal').length,
      highStockCount: products.filter(p => p.status === 'Yüksek').length,
      inventoryHealthScore: Math.floor(65 + Math.random() * 20)
    },
    warehouses,
    categories
  };
};

// Kategori bazında stok dağılımı verileri
const generateCategoryDistribution = (products) => {
  const categoryMap = {};
  
  products.forEach(product => {
    if (!categoryMap[product.category]) {
      categoryMap[product.category] = {
        name: product.category,
        value: 0,
        stockValue: 0
      };
    }
    
    categoryMap[product.category].value++;
    categoryMap[product.category].stockValue += product.stockValue;
  });
  
  return Object.values(categoryMap);
};

// Durum dağılımı
const generateStatusDistribution = (products) => {
  const statusCount = {
    'Kritik': { name: 'Kritik', value: 0 },
    'Düşük': { name: 'Düşük', value: 0 },
    'Normal': { name: 'Normal', value: 0 },
    'Yüksek': { name: 'Yüksek', value: 0 }
  };
  
  products.forEach(product => {
    statusCount[product.status].value++;
  });
  
  return Object.values(statusCount);
};

// Aylık stok değişimi
const generateMonthlyStockHistory = () => {
  const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
  const data = [];
  
  let inStock = 4000;
  let sales = 0;
  let purchases = 0;
  
  months.forEach((month, index) => {
    sales = Math.round(inStock * (0.2 + Math.random() * 0.3));
    purchases = Math.round(sales * (0.8 + Math.random() * 0.5));
    inStock = inStock - sales + purchases;
    
    data.push({
      month,
      inStock,
      sales,
      purchases
    });
  });
  
  return data;
};

// Depo bazında dağılım fonksiyonu
const generateWarehouseDistribution = (products) => {
  const warehouseMap = {};
  
  products.forEach(product => {
    if (!warehouseMap[product.warehouse]) {
      warehouseMap[product.warehouse] = {
        name: product.warehouse,
        value: 0,
        stockValue: 0
      };
    }
    
    warehouseMap[product.warehouse].value++;
    warehouseMap[product.warehouse].stockValue += product.stockValue;
  });
  
  return Object.values(warehouseMap);
};

// Optimizasyon seviyeleri için açıklamalar ekleyelim
const OPTIMIZATION_LEVELS = {
  cost: {
    title: 'Maliyet Odaklı',
    description: 'Minimum stok maliyeti hedeflenir',
    impact: 'Stok seviyelerinde %15-20 azalma'
  },
  balanced: {
    title: 'Dengeli',
    description: 'Maliyet ve servis seviyesi dengesi',
    impact: 'Stok maliyetlerinde %10 azalma, %98 servis seviyesi'
  },
  service: {
    title: 'Servis Odaklı',
    description: 'Maksimum müşteri memnuniyeti hedeflenir',
    impact: 'Stok dışı kalma oranında %95 azalma'
  }
};

const StokEnvanterYonetimi = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [turnoverThreshold, setTurnoverThreshold] = useState([0, 5]);
  const [optimizationLevel, setOptimizationLevel] = useState(50);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResults, setOptimizationResults] = useState(null);

  // Veri yükleme işlemi
  useEffect(() => {
    setLoading(true);
    
    // Simüle edilmiş veri yükleme gecikmesi
    const timer = setTimeout(() => {
      const data = generateInventoryData();
      setInventoryData(data);
      setLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Tab değişimi işleyicisi
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Stok verilerini kategoriye, depoya ve duruma göre filtrele
  const filterProducts = () => {
    if (!inventoryData) return [];
    
    return inventoryData.products.filter(product => {
      const matchesSearch = (
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      // Kategori filtresi
      const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
      
      // Depo filtresi
      const matchesWarehouse = filterWarehouse === 'all' || product.warehouse === filterWarehouse;
      
      // Durum filtresi
      const matchesStatus = filterStatus === 'all' || product.status === filterStatus;
      
      // Stok devir hızı filtresi
      const matchesTurnover = 
        parseFloat(product.turnoverRate) >= turnoverThreshold[0] && 
        parseFloat(product.turnoverRate) <= turnoverThreshold[1];
      
      return matchesSearch && matchesCategory && matchesWarehouse && matchesStatus && matchesTurnover;
    });
  };

  // Ürünleri sıralama
  const sortProducts = (products) => {
    return [...products].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'currentStock':
          comparison = a.currentStock - b.currentStock;
          break;
        case 'status':
          // Kritik > Düşük > Yüksek > Normal
          const statusOrder = { 'Kritik': 0, 'Düşük': 1, 'Yüksek': 2, 'Normal': 3 };
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
        case 'turnoverRate':
          comparison = parseFloat(a.turnoverRate) - parseFloat(b.turnoverRate);
          break;
        case 'stockValue':
          comparison = a.stockValue - b.stockValue;
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Filtrelenmiş ve sıralanmış ürünler
  const filteredProducts = inventoryData ? sortProducts(filterProducts()) : [];

  // Kategori dağılımı verileri
  const categoryData = inventoryData ? generateCategoryDistribution(inventoryData.products) : [];
  
  // Durum dağılımı verileri
  const statusData = inventoryData ? generateStatusDistribution(inventoryData.products) : [];
  
  // Aylık stok değişimi verileri
  const monthlyData = generateMonthlyStockHistory();
  
  // Depo dağılımı verileri
  const warehouseData = inventoryData ? generateWarehouseDistribution(inventoryData.products) : [];

  // Stok değeri ve miktarı karşılaştırması
  const generateStockValueVsQuantityData = () => {
    if (!inventoryData) return [];
    
    return inventoryData.products.map(product => ({
      name: product.name.length > 10 ? product.name.substring(0, 10) + '...' : product.name,
      currentStock: product.currentStock,
      stockValue: product.stockValue / 1000, // bin TL cinsinden
      category: product.category
    }));
  };

  // Stok sağlık skoru rengi hesaplama
  const getHealthScoreColor = (score) => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#8bc34a';
    if (score >= 40) return '#ff9800';
    return '#f44336';
  };

  // Optimizasyon uygulama fonksiyonu
  const handleApplyOptimization = () => {
    setIsOptimizing(true);
    
    // Simüle edilmiş optimizasyon işlemi
    setTimeout(() => {
      const results = {
        stockReduction: Math.floor(15 + Math.random() * 10), // %15-25 arası azalma
        costSaving: Math.floor(500000 + Math.random() * 500000), // 500bin-1milyon arası tasarruf
        serviceLevel: (97 + Math.random() * 2).toFixed(1), // %97-99 arası servis seviyesi
        recommendations: [
          { 
            product: 'Laptop',
            currentStock: 120,
            recommendedStock: 85,
            saving: 87500
          },
          { 
            product: 'Akıllı Telefon',
            currentStock: 200,
            recommendedStock: 150,
            saving: 125000
          },
          // ...diğer öneriler
        ]
      };
      
      setOptimizationResults(results);
      setIsOptimizing(false);
    }, 2000);
  };

  // Tedarik Planlaması Tab İçeriği güncelleme
  const renderSupplyPlanningTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Stok Optimizasyonu
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Optimizasyon seviyesini seçin ve stok planlamanızı otomatik olarak optimize edin.
          </Typography>
          
          {/* Optimizasyon Seviyeleri */}
          <Stack spacing={2}>
            {Object.entries(OPTIMIZATION_LEVELS).map(([key, level]) => (
              <Paper
                key={key}
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: optimizationLevel === key ? 'primary.main' : 'divider',
                  bgcolor: optimizationLevel === key ? 'primary.lighter' : 'background.paper',
                  cursor: 'pointer'
                }}
                onClick={() => setOptimizationLevel(key)}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {level.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {level.description}
                </Typography>
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Beklenen etki: {level.impact}
                </Typography>
              </Paper>
            ))}
          </Stack>
          
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleApplyOptimization}
            disabled={isOptimizing}
          >
            {isOptimizing ? 'Optimizasyon Uygulanıyor...' : 'Optimizasyonu Uygula'}
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {optimizationResults ? (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Optimizasyon Sonuçları
            </Typography>
            
            {/* Özet Metrikler */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.lighter', borderRadius: 2 }}>
                  <Typography variant="h4" color="success.main">
                    %{optimizationResults.stockReduction}
                  </Typography>
                  <Typography variant="body2">Stok Azalması</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.lighter', borderRadius: 2 }}>
                  <Typography variant="h4" color="primary.main">
                    ₺{(optimizationResults.costSaving/1000).toFixed(0)}K
                  </Typography>
                  <Typography variant="body2">Maliyet Tasarrufu</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.lighter', borderRadius: 2 }}>
                  <Typography variant="h4" color="info.main">
                    %{optimizationResults.serviceLevel}
                  </Typography>
                  <Typography variant="body2">Servis Seviyesi</Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Ürün Bazlı Öneriler */}
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 4 }}>
              Stok Optimizasyon Önerileri
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ürün</TableCell>
                    <TableCell align="right">Mevcut Stok</TableCell>
                    <TableCell align="right">Önerilen Stok</TableCell>
                    <TableCell align="right">Tahmini Tasarruf</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {optimizationResults.recommendations.map((item) => (
                    <TableRow key={item.product}>
                      <TableCell>{item.product}</TableCell>
                      <TableCell align="right">{item.currentStock}</TableCell>
                      <TableCell align="right">{item.recommendedStock}</TableCell>
                      <TableCell align="right">₺{item.saving.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="body1" color="text.secondary">
              Optimizasyon seviyesini seçin ve "Optimizasyonu Uygula" butonuna tıklayın
            </Typography>
          </Paper>
        )}
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }} className="slide-in-left">
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Stok Envanter Yönetimi
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" paragraph>
          Stok seviyelerini optimize edin, maliyetleri düşürün ve müşteri memnuniyetini artırın.
        </Typography>
      </Box>

      {/* Yükleniyor durumu */}
      {loading && (
        <Box sx={{ width: '100%', my: 4 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            Stok verileri yükleniyor...
          </Typography>
        </Box>
      )}

      {/* Ana içerik */}
      {!loading && inventoryData && (
        <>
          {/* Özet Kartlar */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Toplam Stok Değeri
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    <AnimatedNumber 
                      value={inventoryData.summary.totalInventoryValue} 
                      formatter={(value) => `₺${Number(value).toLocaleString('tr-TR')}`}
                      duration={2000}
                    />
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {inventoryData.summary.totalProducts} ürün / {inventoryData.warehouses.length} depo
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stok Sağlık Skoru
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                      variant="h4" 
                      component="div" 
                      fontWeight="bold"
                      sx={{ color: getHealthScoreColor(inventoryData.summary.inventoryHealthScore) }}
                    >
                      <AnimatedNumber 
                        value={inventoryData.summary.inventoryHealthScore} 
                        suffix="%" 
                        duration={2000}
                      />
                    </Typography>
                    <Box sx={{ ml: 1, mt: 0.5 }}>
                      <Tooltip title="Stok sağlık skoru, envanter performansını gösteren bileşik bir metriktir.">
                        <InsightsIcon color="action" fontSize="small" />
                      </Tooltip>
                    </Box>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={inventoryData.summary.inventoryHealthScore} 
                    color={inventoryData.summary.inventoryHealthScore >= 60 ? "success" : "warning"}
                    sx={{ mt: 1, height: 8, borderRadius: 1 }} 
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stok Devir Hızı (SDH)
                  </Typography>
                  <Tooltip title="Aylık ortalama satış miktarının ortalama stok miktarına oranı. Örn: 2.5 değeri, stoklarınızın ayda ortalama 2.5 kez yenilendiğini gösterir.">
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Typography variant="h4" component="div" fontWeight="bold">
                          <AnimatedNumber value={inventoryData.summary.avgTurnoverRate} duration={2000} />
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, ml: 0.5 }}>
                          devir/ay
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                      %8,3 artış
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Stok Durumu
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Chip 
                      label={`${inventoryData.summary.criticalStockCount} Kritik`}
                      size="small"
                      color="error"
                      icon={<WarningAmberIcon />}
                      sx={{ fontWeight: 'bold' }}
                    />
                    <Chip 
                      label={`${inventoryData.summary.lowStockCount} Düşük`}
                      size="small"
                      color="warning"
                      sx={{ fontWeight: 'bold' }}
                    />
                    <Chip 
                      label={`${inventoryData.summary.highStockCount} Yüksek`}
                      size="small"
                      color="info"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                      {inventoryData.summary.optimizedStockCount} ürün optimum seviyede
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Ana Sekmeler */}
          <Paper sx={{ borderRadius: 2, mb: 4 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab 
                label="Stok Analizi" 
                icon={<InsightsIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Envanter Yönetimi" 
                icon={<InventoryIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Tedarik Planlaması" 
                icon={<LocalShippingIcon />} 
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          {/* Stok Analizi Tab İçeriği */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: 'linear-gradient(90deg, #4caf50, #8bc34a)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Stok Değeri ve Hareket Trendi
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Yenile">
                        <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="CSV İndir">
                        <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  
                  <Box sx={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={monthlyData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="inStock" 
                          name="Stok Seviyesi" 
                          stroke="#4caf50" 
                          fill="#4caf50" 
                          fillOpacity={0.2} 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="sales" 
                          name="Satışlar" 
                          stroke="#f44336" 
                          fill="#f44336" 
                          fillOpacity={0.2} 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="purchases" 
                          name="Alımlar" 
                          stroke="#2196f3" 
                          fill="#2196f3" 
                          fillOpacity={0.2} 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} lg={4}>
                <Stack spacing={3}>
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Kategori Dağılımı
                    </Typography>
                    <Box sx={{ height: 150 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={categoryData}
                            cx="50%"
                            cy="50%"
                            innerRadius={45}
                            outerRadius={60}
                            paddingAngle={3}
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {categoryData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                  
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Stok Durum Dağılımı
                    </Typography>
                    <Box sx={{ height: 150 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={statusData}
                          margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={70} />
                          <RechartsTooltip />
                          <Bar dataKey="value" name="Ürün Sayısı">
                            {statusData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={
                                  entry.name === 'Kritik' ? '#f44336' : 
                                  entry.name === 'Düşük' ? '#ff9800' : 
                                  entry.name === 'Yüksek' ? '#2196f3' : 
                                  '#4caf50'
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Depo Bazında Stok Dağılımı
                  </Typography>
                  <Box sx={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={warehouseData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="value" name="Ürün Sayısı" fill="#8884d8" />
                        <Bar dataKey="stockValue" name="Stok Değeri (bin ₺)" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Stok Değeri vs. Miktar Karşılaştırması
                  </Typography>
                  <Box sx={{ height: 250 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart
                        margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="currentStock" 
                          name="Stok Miktarı" 
                          label={{ value: 'Stok Miktarı', position: 'bottom', offset: 0 }}
                        />
                        <YAxis 
                          dataKey="stockValue" 
                          name="Stok Değeri (bin ₺)" 
                          label={{ value: 'Stok Değeri (bin ₺)', angle: -90, position: 'insideLeft' }}
                        />
                        <ZAxis dataKey="category" name="Kategori" />
                        <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter 
                          name="Ürünler" 
                          data={generateStockValueVsQuantityData()} 
                          fill="#8884d8" 
                        />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Envanter Yönetimi Tab İçeriği */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Ürün Listesi
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        size="small"
                        placeholder="Ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      {/* Tek bir FormControl bloğu */}
                      {[
                        { label: 'Kategori', value: filterCategory, setter: setFilterCategory, items: inventoryData.categories },
                        { label: 'Depo', value: filterWarehouse, setter: setFilterWarehouse, items: inventoryData.warehouses },
                        { 
                          label: 'Durum', 
                          value: filterStatus, 
                          setter: setFilterStatus, 
                          items: [
                            { value: 'Kritik', label: 'Kritik' },
                            { value: 'Düşük', label: 'Düşük' },
                            { value: 'Normal', label: 'Normal' },
                            { value: 'Yüksek', label: 'Yüksek' }
                          ]
                        }
                      ].map(({ label, value, setter, items }) => (
                        <FormControl key={label} size="small">
                          <InputLabel>{label}</InputLabel>
                          <Select
                            value={value}
                            onChange={(e) => setter(e.target.value)}
                            label={label}
                          >
                            <MenuItem value="all">Tümü</MenuItem>
                            {items.map((item, index) => (
                              <MenuItem key={index} value={typeof item === 'object' ? item.value : item}>
                                {typeof item === 'object' ? item.label : item}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ))}
                      <Tooltip title="Filtrele">
                        <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sırala">
                        <IconButton size="small" sx={{ border: '1px solid #ddd' }}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Ürün Adı</TableCell>
                          <TableCell>Kategori</TableCell>
                          <TableCell>Depo</TableCell>
                          <TableCell>Stok Miktarı</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Stok Devir Hızı</TableCell>
                          <TableCell>Kar Marjı</TableCell>
                          <TableCell>Stok Değeri</TableCell>
                          <TableCell>Son Güncelleme</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.category}</TableCell>
                            <TableCell>{product.warehouse}</TableCell>
                            <TableCell>{product.currentStock}</TableCell>
                            <TableCell>
                              <Chip 
                                label={product.status} 
                                size="small" 
                                color={
                                  product.status === 'Kritik' ? 'error' : 
                                  product.status === 'Düşük' ? 'warning' : 
                                  product.status === 'Yüksek' ? 'info' : 
                                  'success'
                                }
                              />
                            </TableCell>
                            <TableCell>{product.turnoverRate}</TableCell>
                            <TableCell>{product.profitMargin}%</TableCell>
                            <TableCell>₺{product.stockValue.toLocaleString('tr-TR')}</TableCell>
                            <TableCell>{product.lastUpdated}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tedarik Planlaması Tab İçeriği */}
          {activeTab === 2 && renderSupplyPlanningTab()}
        </>
      )}
    </Container>
  );
};

export default StokEnvanterYonetimi;